// frontend/src/perf/AskTimbo.js
// Question bar at the top of the Performance tab.
// All computation happens in the backend (/api/ask): this is input, suggestions and rendering only.
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { thumbUrl } from './metaApiSource';
import '../shared/layout.css';

const API_BASE = process.env.REACT_APP_API_URL || 'https://timbo-production.up.railway.app';

const PLACEHOLDERS = [
  'Ask anything about performance…',
  'Why did CPA go up since July 21?',
  'Which creator converts best over 3 months?',
  'Main adset week by week',
  'Which winners are not in Main yet?',
];

// Prompt starters: sentence openings, not canned questions.
const SEEDS = [
  { t: 'Performance of ', h: 'a creative (ID or name)' },
  { t: 'How is ', h: 'a creator doing' },
  { t: 'Compare ', h: 'two periods' },
  { t: 'Why ', h: 'a change' },
  { t: 'Week by week ', h: 'trend, fatigue' },
];

const INTENTS = [
  { re: /why|explain|drop|down|up|declin|chang|pourq/, t: 'Why did CPA go up over the last 4 weeks?', h: 'explanation' },
  { re: /pause|july|before|after/, t: 'Compare before and after July 21', h: 'comparison' },
  { re: /fatig|aging|trend|evol/, t: 'Week by week, with the share of aging creatives', h: 'trend' },
  { re: /winner|promo|main/, t: 'Which winners are not in Main yet?', h: 'lookup' },
  { re: /week|this|last/, t: 'This week vs last week', h: 'comparison' },
  { re: /best|worst|top|rank/, t: 'Top 10 creatives by CPA over 4 weeks', h: 'ranking' },
];

function BrainIcon({ size = 24 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9.5 3.5A2.5 2.5 0 0 0 7 6a2.5 2.5 0 0 0-2 4 2.5 2.5 0 0 0 .5 4.5A2.5 2.5 0 0 0 8 19a2.5 2.5 0 0 0 4 1.2V4.7a2.5 2.5 0 0 0-2.5-1.2Z" />
      <path d="M14.5 3.5A2.5 2.5 0 0 1 17 6a2.5 2.5 0 0 1 2 4 2.5 2.5 0 0 1-.5 4.5A2.5 2.5 0 0 1 16 19a2.5 2.5 0 0 1-4 1.2V4.7a2.5 2.5 0 0 1 2.5-1.2Z" />
      <path d="M9 8.5h1M14 8.5h1M9.5 12.5h1M13.5 12.5h1M10 16.5h1M13 16.5h1" />
    </svg>
  );
}

// The bar, the answer shell and its ⟳ / ✕ icons are the shared .tb-ask-* classes
// (shared/layout.css), identical on Scan. This string only styles the answer's content.
const CSS = `
.ask-lead{font-size:16px;line-height:1.6;margin:0 0 12px;max-width:92ch;color:#0a0a0a;letter-spacing:-.005em}
.ask-list{margin:0 0 14px;padding:0;list-style:none}
.ask-list li{position:relative;padding:4px 0 4px 17px;font-size:13.5px;line-height:1.6;color:#333;max-width:92ch}
.ask-list li:before{content:'';position:absolute;left:3px;top:12px;width:5px;height:5px;border-radius:50%;background:#FF7900}
.ask-sugg{border:1px solid #e6e6e6;border-left:2px solid #FF7900;background:rgba(255,121,0,.06);border-radius:0 9px 9px 0;padding:10px 14px;margin:0 0 14px}
.ask-sugg .l{font-size:10px;font-weight:700;color:#b25a00;text-transform:uppercase;letter-spacing:.06em;margin-bottom:3px}
.ask-sugg div.t{font-size:13.5px;line-height:1.55;color:#5a3a12}
.ask-kpis{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:12px;margin-bottom:18px}
.ask-k{border:1px solid #eee;border-radius:11px;padding:13px 15px;min-width:0}
.ask-k .l{font-size:10.5px;color:#FF7900;font-weight:700;text-transform:uppercase;letter-spacing:.05em}
.ask-k .v{font-size:26px;font-weight:700;margin-top:4px;letter-spacing:-.03em;line-height:1.15;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.ask-k .s{font-size:12px;color:#999;margin-top:3px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.ask-k .s .up{color:#1a7f4b;font-weight:500}
.ask-k .s .dn{color:#c0392b;font-weight:500}
.ask-tt{display:flex;align-items:center;gap:8px;margin:2px 0 10px;flex-wrap:wrap}
.ask-tt .n{font-size:11px;font-weight:700;color:#555;text-transform:uppercase;letter-spacing:.05em;margin-right:2px}
.ask-dim{font:inherit;font-size:12px;padding:5px 12px;border:1px solid #e5e5e5;border-radius:999px;background:#fff;color:#666;cursor:pointer}
.ask-dim:hover{border-color:rgba(255,121,0,.35);color:#FF7900}
.ask-dim.on{border-color:#FF7900;color:#FF7900;background:rgba(255,121,0,.06);font-weight:500}
.ask-scroll{overflow-x:auto}
.ask-tbl{width:100%;border-collapse:collapse;font-size:13.5px}
.ask-tbl th{text-align:left;font-weight:600;color:#888;font-size:10.5px;text-transform:uppercase;letter-spacing:.05em;padding:8px 14px;border-bottom:1px solid #eee;white-space:nowrap}
.ask-tbl td{padding:11px 14px;border-bottom:1px solid #f4f4f4;white-space:nowrap}
.ask-tbl tbody tr:last-child td{border-bottom:0}
.ask-tbl tbody tr:hover{background:rgba(255,121,0,.04)}
.ask-tbl .n{text-align:right;font-variant-numeric:tabular-nums}
.ask-tbl .th{display:flex;align-items:center;gap:10px;font-weight:500}
.ask-tbl .th img{width:24px;height:40px;object-fit:cover;border-radius:4px;background:#f0f0f0;cursor:pointer}
.ask-imp{font-variant-numeric:tabular-nums;font-weight:600}
.ask-imp.p{color:#1a7f4b}
.ask-imp.m{color:#c0392b}
.ask-bar-i{display:inline-block;height:7px;border-radius:4px;vertical-align:middle;margin-right:9px}
.ask-bar-i.p{background:#1a7f4b}
.ask-bar-i.m{background:#c0392b}
.ask-note{font-size:11.5px;color:#999;margin-top:11px;line-height:1.55;max-width:110ch}
.ask-src{margin-top:12px;display:flex;gap:7px;flex-wrap:wrap;align-items:center;font-size:11px;color:#aaa}
.ask-src span.p{border:1px solid #eee;border-radius:5px;padding:3px 8px;color:#777}
.ask-gap{border:1px dashed #ddd;background:#f7f7f7;border-radius:9px;padding:11px 13px;font-size:13px;color:#666;line-height:1.55;margin-top:11px;max-width:100ch}
.ask-err{border:1px solid #f3c2c2;background:#fdf3f3;border-radius:9px;padding:10px 13px;font-size:13.5px;color:#a12a2a}
.ask-degraded{border:1px solid #f3c2c2;background:#fdf3f3;border-radius:8px;padding:9px 13px;font-size:12.5px;font-weight:600;color:#c0392b;margin:0 0 12px}
.ask-wait{display:flex;align-items:center;gap:10px;font-size:13.5px;color:#888}
.ask-dots{display:flex;gap:4px}
.ask-dots i{width:5px;height:5px;border-radius:50%;background:#bbb;animation:askbob 1s infinite}
.ask-dots i:nth-child(2){animation-delay:.15s}.ask-dots i:nth-child(3){animation-delay:.3s}
@keyframes askbob{0%,60%,100%{opacity:.3;transform:none}30%{opacity:1;transform:translateY(-3px)}}
@media (max-width:1000px){.ask-kpis{grid-template-columns:repeat(2,minmax(0,1fr))}}
@media (max-width:620px){.ask-kpis{grid-template-columns:1fr}}
@media (prefers-reduced-motion:reduce){.ask-dots i{animation:none}}
`;

function useStyleOnce() {
  useEffect(() => {
    const el = document.getElementById('ask-timbo-css');
    if (el) { el.textContent = CSS; return; }
    const s = document.createElement('style');
    s.id = 'ask-timbo-css';
    s.textContent = CSS;
    document.head.appendChild(s);
  }, []);
}

// The lead contains <b>…</b>: only that markup is rendered, everything else is escaped.
function RichText({ text }) {
  const parts = String(text || '').split(/(<b>.*?<\/b>)/g);
  return (
    <>
      {parts.map((p, i) => {
        const m = p.match(/^<b>(.*?)<\/b>$/);
        return m ? <b key={i}>{m[1]}</b> : <React.Fragment key={i}>{p}</React.Fragment>;
      })}
    </>
  );
}

// KPI sub-lines carry a signed percentage ("−36% vs before"). Colour it by whether the
// change is good for that metric: for cost-like metrics a decrease is good, elsewhere it is
// an increase. Anything we cannot read stays neutral grey.
const COST_METRIC = /cpa|cost|spend|lost/i;
function KpiSub({ label, text }) {
  const s = String(text || '');
  const m = s.match(/([+\-−–])\s?(\d+(?:[.,]\d+)?)\s?%/);
  if (!m) return <>{s}</>;
  const down = m[1] !== '+';
  const good = COST_METRIC.test(label || '') ? down : !down;
  const i = s.indexOf(m[0]);
  return (
    <>
      {s.slice(0, i)}
      <span className={good ? 'up' : 'dn'}>{m[0]}</span>
      {s.slice(i + m[0].length)}
    </>
  );
}

// Signed cells ("+1,533" / "−240") in an impact column get a colour and a proportional bar:
// green when the group gained purchases, red when it lost them.
function parseSigned(v) {
  const s = String(v == null ? '' : v).trim();
  const m = s.match(/^([+\-−–])\s?([\d.,\s\u202f]+)$/);
  if (!m) return null;
  const n = parseFloat(m[2].replace(/[\s,\u202f]/g, '').replace(',', '.'));
  if (isNaN(n)) return null;
  return { pos: m[1] === '+', n };
}

export default function AskTimbo({ onPlay }) {
  useStyleOnce();
  const [q, setQ] = useState('');
  const [focus, setFocus] = useState(false);
  const [sel, setSel] = useState(-1);
  const [phIdx, setPhIdx] = useState(0);
  const [entities, setEntities] = useState({ ready: false, creators: [], features: [], ads: [] });
  const [busy, setBusy] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [res, setRes] = useState(null);
  const [err, setErr] = useState(null);
  const [lastQ, setLastQ] = useState('');
  const boxRef = useRef(null);
  const inputRef = useRef(null);

  // Preload 16 weeks of Meta data as soon as the tab opens.
  useEffect(() => {
    fetch(`${API_BASE}/api/ask/warmup`, { method: 'POST' }).catch(() => {});
  }, []);

  useEffect(() => {
    if (q) return undefined;
    const t = setInterval(() => setPhIdx((i) => (i + 1) % PLACEHOLDERS.length), 3400);
    return () => clearInterval(t);
  }, [q]);

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setFocus(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const loadEntities = useCallback(() => {
    if (entities.ready) return;
    fetch(`${API_BASE}/api/ask/entities`).then((r) => r.json()).then((d) => { if (d && d.ready) setEntities(d); }).catch(() => {});
  }, [entities.ready]);

  useEffect(() => {
    if (!busy) return undefined;
    const t0 = Date.now();
    const t = setInterval(() => setElapsed(Math.round((Date.now() - t0) / 1000)), 500);
    return () => clearInterval(t);
  }, [busy]);

  const groups = useMemo(() => {
    const v = q.trim().toLowerCase();
    if (!v) return [{ label: 'Start with', items: SEEDS.map((s) => ({ html: <>{s.t}<em>…</em></>, stub: s.t, h: s.h })) }];
    const has = (x) => String(x).toLowerCase().includes(v) && String(x).toLowerCase() !== v;
    const out = [];
    const ads = entities.ads.filter((a) => a.id.startsWith(v) || has(a.label)).slice(0, 4)
      .map((a) => ({ html: <>Performance of <em>{a.label}</em> <span style={{ color: '#bbb' }}>#{a.id}</span></>, full: `Performance of creative ${a.id}`, h: 'creative' }));
    const crs = entities.creators.filter(has).slice(0, 3)
      .map((c) => ({ html: <>How is <em>{c}</em> doing over 3 months</>, full: `How is ${c} doing over 3 months`, h: 'creator' }));
    const fes = entities.features.filter(has).slice(0, 2)
      .map((f) => ({ html: <><em>{f}</em> week by week</>, full: `${f} week by week`, h: 'feature' }));
    const its = INTENTS.filter((i) => i.re.test(v)).slice(0, 3).map((i) => ({ html: <>{i.t}</>, full: i.t, h: i.h }));
    if (its.length) out.push({ label: 'Questions', items: its });
    if (ads.length) out.push({ label: 'Creatives', items: ads });
    if (crs.length) out.push({ label: 'Creators', items: crs });
    if (fes.length) out.push({ label: 'Features', items: fes });
    return out;
  }, [q, entities]);
  const flat = useMemo(() => groups.flatMap((g) => g.items), [groups]);

  const ask = useCallback(async (question, extra) => {
    const text = String(question || '').trim();
    if (!text || busy) return;
    setFocus(false); setBusy(true); setErr(null); setElapsed(0); setLastQ(text);
    try {
      const r = await fetch(`${API_BASE}/api/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(Object.assign({ question: text }, extra || {})),
      });
      const d = await r.json().catch(() => null);
      if (!r.ok || !d || !d.ok) throw new Error((d && d.message) || `Error ${r.status}`);
      setRes(d);
    } catch (e) {
      setErr(e.message || 'Unknown error');
    } finally {
      setBusy(false);
    }
  }, [busy]);

  const pick = (it) => {
    if (it.stub) { setQ(it.stub); setSel(-1); setTimeout(() => inputRef.current && inputRef.current.focus(), 0); return; }
    setQ(it.full); ask(it.full);
  };

  const onKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setFocus(true); setSel((s) => Math.min(s + 1, flat.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSel((s) => Math.max(s - 1, -1)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (sel >= 0 && flat[sel] && focus) pick(flat[sel]); else ask(q); }
    else if (e.key === 'Escape') { setFocus(false); }
  };

  const clear = () => { setRes(null); setErr(null); setQ(''); };
  const t = res && res.table;

  // Impact column: the one whose header mentions impact or lost purchases.
  const impactCol = useMemo(() => {
    if (!t || !t.h) return -1;
    return t.h.findIndex((h) => /impact|lost/i.test(String(h)));
  }, [t]);
  const impactMax = useMemo(() => {
    if (!t || impactCol < 0) return 0;
    return t.r.reduce((m, row) => {
      const p = parseSigned(row[impactCol]);
      return p ? Math.max(m, Math.abs(p.n)) : m;
    }, 0);
  }, [t, impactCol]);

  let idx = -1;

  return (
    <div className="tb-ask">
      <div className={'tb-ask-unit' + (focus ? ' focus' : '')}>
        <div className="tb-ask-box" ref={boxRef}>
          <div className="tb-ask-bar">
            <span className={'tb-ask-brain' + (busy ? ' busy' : '')}><BrainIcon size={25} /></span>
            <input
              ref={inputRef}
              className="tb-ask-input"
              value={q}
              aria-label="Ask Timbo"
              autoComplete="off"
              spellCheck={false}
              onChange={(e) => { setQ(e.target.value); setSel(-1); setFocus(true); }}
              onFocus={() => { setFocus(true); loadEntities(); }}
              onKeyDown={onKey}
            />
            {!q && <span className="tb-ask-ph">{PLACEHOLDERS[phIdx]}</span>}
            <button type="button" className="tb-ask-go" disabled={busy} onClick={() => (q.trim() ? ask(q) : inputRef.current && inputRef.current.focus())}>
              {busy ? '…' : 'Ask Timbo'}
            </button>
          </div>
          {focus && (
            <div className="tb-ask-sug">
              {groups.length === 0 && <div className="tb-ask-sempty">No match. Send it anyway, I'll try to figure it out.</div>}
              {groups.map((g) => (
                <div className="tb-ask-sg" key={g.label}>
                  <div className="tb-ask-sl">{g.label}</div>
                  {g.items.map((it) => { idx += 1; const i = idx; return (
                    <div key={i} className={'tb-ask-sr' + (i === sel ? ' sel' : '')} onMouseDown={(e) => { e.preventDefault(); pick(it); }}>
                      <span>{it.html}</span><span className="h">{it.h}</span>
                    </div>
                  ); })}
                </div>
              ))}
              {!q && (
                <div className="tb-ask-sempty">
                  Type an ID, a creator, a feature or a period — suggestions adapt.
                  {' '}<span className="tb-ask-kbd">↑↓</span> to navigate, <span className="tb-ask-kbd">↵</span> to send.
                  {!entities.ready && ' Loading creatives…'}
                </div>
              )}
            </div>
          )}
        </div>

      {(busy || res || err) && (
        <div className="tb-ask-ans tb-ask-clearfix">
          <div className="tb-ask-tools">
            {(res || err) && !busy && <button type="button" className="tb-ask-ic" onClick={() => ask(lastQ, { fresh: true })} title="Re-run this question on fresh Meta data" aria-label="Re-run on fresh data">⟳</button>}
            <button type="button" className="tb-ask-ic" onClick={clear} title="Clear the answer" aria-label="Clear">✕</button>
          </div>
          <div>
            {busy && (
              <div className="ask-wait">
                <span className="ask-dots"><i /><i /><i /></span>
                {elapsed < 6 ? 'Reading the question…' : elapsed < 20 ? 'Crunching the last 16 weeks…' : `First Meta load (${elapsed}s). Next questions will be instant.`}
              </div>
            )}
            {!busy && err && <div className="ask-err">{err}</div>}
            {!busy && !err && res && (
              <>
                {res.answer && res.answer.degraded && (
                  <div className="ask-degraded">Answer written from raw numbers: the model call failed ({res.answer.degraded}{res.answer.degraded_message ? ' — ' + res.answer.degraded_message : ''}).</div>
                )}
                <p className="ask-lead"><RichText text={res.answer && res.answer.lead} /></p>
                {res.answer && res.answer.insights && res.answer.insights.length > 0 && (
                  <ul className="ask-list">{res.answer.insights.map((x, i) => <li key={i}><RichText text={x} /></li>)}</ul>
                )}
                {res.answer && res.answer.suggestions && res.answer.suggestions.length > 0 && (
                  <div className="ask-sugg">
                    <div className="l">Worth a look</div>
                    {res.answer.suggestions.map((x, i) => <div className="t" key={i}><RichText text={x} /></div>)}
                  </div>
                )}
                {res.kpis && res.kpis.length > 0 && (
                  <div className="ask-kpis">
                    {res.kpis.map((k, i) => (
                      <div className="ask-k" key={i}>
                        <div className="l">{k[0]}</div>
                        <div className="v" title={k[1]}>{k[1]}</div>
                        <div className="s" title={k[2]}><KpiSub label={k[0]} text={k[2]} /></div>
                      </div>
                    ))}
                  </div>
                )}
                {t && t.r && t.r.length > 0 && (
                  <>
                    {(t.title || (t.dims && t.dims.length > 0)) && (
                      <div className="ask-tt">
                        {t.title && <span className="n">{t.title}</span>}
                        {t.dims && t.dims.map((d) => (
                          <button type="button" key={d.key} className={'ask-dim' + (d.key === t.activeDim ? ' on' : '')}
                            onClick={() => d.key !== t.activeDim && ask(lastQ, { dimension: d.key })}>{d.label}</button>
                        ))}
                      </div>
                    )}
                    <div className="ask-scroll">
                      <table className="ask-tbl">
                        <thead><tr>{t.h.map((c, i) => <th key={i} className={i > 1 ? 'n' : ''}>{c}</th>)}</tr></thead>
                        <tbody>
                          {t.r.map((row, ri) => {
                            const adId = t.adIds && t.adIds[ri];
                            return (
                              <tr key={ri}>
                                {row.map((c, ci) => {
                                  const imp = ci === impactCol ? parseSigned(c) : null;
                                  return (
                                    <td key={ci} className={ci > 1 ? 'n' : ''}>
                                      {ci === 0 && adId ? (
                                        <span className="th">
                                          <img src={thumbUrl({ ad_id: adId })} alt="" loading="lazy" onClick={() => onPlay && onPlay(adId)} />
                                          {c}
                                        </span>
                                      ) : imp ? (
                                        <>
                                          <span
                                            className={'ask-bar-i ' + (imp.pos ? 'p' : 'm')}
                                            style={{ width: impactMax ? Math.max(3, Math.round((Math.abs(imp.n) / impactMax) * 70)) : 3 }}
                                          />
                                          <span className={'ask-imp ' + (imp.pos ? 'p' : 'm')}>{c}</span>
                                        </>
                                      ) : c}
                                    </td>
                                  );
                                })}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {t.note && <div className="ask-note">{t.note}</div>}
                  </>
                )}
                {res.gap && <div className="ask-gap">{res.gap}</div>}
                {res.sources && res.sources.length > 0 && (
                  <div className="ask-src">Based on: {res.sources.map((s, i) => <span className="p" key={i}>{s}</span>)}</div>
                )}
              </>
            )}
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
